import type { StepLogRow } from '@ctcells/api'
import type { HistoryEntry } from '@ctcells/contract'
import { executionEventLabel, t } from '@ctcells/i18n'
import type { ReactNode } from 'react'

import { cx } from '../atoms/cx.ts'
import type { Column } from '../atoms/DataTable.tsx'
import { StatusBadge } from '../atoms/StatusBadge.tsx'
import { operationText } from '../screens/batches/batchDetailModel.ts'
import {
  eventColumns,
  eventRowKey,
  isEmphasisEvent,
  isStateChangeEvent,
} from '../screens/batches/eventColumns.tsx'
import { timeSecText } from '../screens/format.ts'
import { stepHeadText } from '../screens/monitoring/latestStepModel.ts'

/**
 * 작업 줄 이벤트 칸(`StripEvents`)의 **행 모델** — 원장 행 + 스텝 줄을 한 목록으로 섞고 갈래로 거른다.
 * 열은 작업 상세 이벤트 탭과 같은 한 벌(`eventColumns`)을 감싼다 — 스텝 줄만 제 칸을 채운다.
 */
export type StripEventsFilter = 'all' | 'step' | 'state' | 'other'

export const STRIP_EVENTS_FILTERS: readonly StripEventsFilter[] = ['all', 'step', 'state', 'other']

/** 한 목록의 행 — 원장 행과 스텝 줄. 🔴 모양을 합치지 않는다(스텝을 원장 행 꼴로 지어내지 않는다) */
export type StripEventRow =
  { kind: 'history'; entry: HistoryEntry } | { kind: 'step'; step: StepLogRow }

/** 시각 내림차순으로 섞고 갈래로 거른다. 시각을 못 읽는 행은 맨 뒤 */
export function stripEventRows(
  history: readonly HistoryEntry[],
  steps: readonly StepLogRow[],
  filter: StripEventsFilter,
): StripEventRow[] {
  const rows: StripEventRow[] = []
  if (filter !== 'step')
    for (const entry of history) {
      const state = isStateChangeEvent(entry)
      if (filter === 'all' || (filter === 'state') === state) rows.push({ kind: 'history', entry })
    }
  if (filter === 'all' || filter === 'step')
    for (const step of steps) rows.push({ kind: 'step', step })
  const time = (row: StripEventRow): number => {
    const ms = Date.parse(row.kind === 'history' ? row.entry.ts : row.step.ts)
    return Number.isNaN(ms) ? -Infinity : ms
  }
  return rows
    .map((row, index) => ({ row, index, at: time(row) }))
    .sort(
      (a, b) =>
        b.at - a.at ||
        (b.row.kind === 'step' ? (b.row.step.receivedOrder ?? b.index) : 0) -
          (a.row.kind === 'step' ? (a.row.step.receivedOrder ?? a.index) : 0) ||
        a.index - b.index,
    )
    .map((x) => x.row)
}

/** 스텝 줄의 「내용」 — 모듈의 말(없으면 생애 라벨) + 「n/N」 */
export function stepContentText(step: StepLogRow): string {
  /* 머리는 장비 그림 아래 줄과 같은 값 — 생애 사건은 모듈 원문 대신 사건 라벨(`stepHeadText` 머리말 · eric 2026-10-12 「취소됐다」) */
  const head = stepHeadText(step)
  return step.step !== undefined && step.total !== undefined
    ? `${head} · ${t('event.content.stepCount', { step: step.step, total: step.total })}`
    : head
}

/**
 * 모듈 이벤트 로그(`ModuleEventLog`)의 「내용」 — 🔴 **무슨 동작인지가 서야 한다**(eric 2026-11-02 「뭘 검사하고 시작했는지가 안 보여」).
 * 이 표에는 동작 열이 없어서 이름을 내용에 싣는다. 작업 줄 · 작업 상세는 동작 열이 따로 있어 이 글자를 쓰지 않는다(`stepContentText`).
 *
 * - 원자 완료: 모듈의 말 그대로(`open_door 완료` — 「완료」는 module-core 원문 · 번역하지 않는다)
 * - 시작 · 끝 등 실행 사건: 우리 사전의 사건 라벨 + 시퀀스 원값 + (묶음이면) 스텝 수. 🔴 모듈 문장(「취소됐다」)은 옮기지 않는다(`stepHeadText` 머리말 · eric 2026-10-12)
 * - 시퀀스 이름은 프레임이 실은 원값이다 — 시작 줄은 같은 실행의 첫 원자 · 끝 줄은 마지막 원자(`stepLog.ts`). 아직 없으면 라벨만
 * ⚠ 묶음 이름 · 대상(자리 · 검사 항목) · 판정은 프레임에 없어 싣지 않는다(실측 2026-11-02 — `result` 는 `{sequence}` 뿐).
 */
export function stepLogText(step: StepLogRow): string {
  if (step.eventType === 'step_complete' && step.message !== undefined) return step.message
  const parts = [executionEventLabel(step.eventType)]
  if (step.action !== undefined) parts.push(step.action)
  if (isRunEvent(step) && step.total !== undefined && step.total > 1)
    parts.push(t('event.content.stepTotal', { total: step.total }))
  return parts.join(' · ')
}

/** 실행 전체의 시작 · 끝 줄인가 — 그 안의 원자 줄과 눈으로 갈리게 한다 */
function isRunEvent(step: StepLogRow): boolean {
  return step.eventType === 'started' || step.eventType === 'complete'
}

function StepLogCell({ step }: { step: StepLogRow }) {
  const text = stepLogText(step)
  return (
    <span
      className={cx('module-events__text', isRunEvent(step) && 'module-events__text--run')}
      title={text}
    >
      {text}
    </span>
  )
}

/** 장비 열의 이름 — 넓으면 `name`, 좁은 칸이면 `short`(`moduleEventLog.css` 의 컨테이너 질의가 고른다) */
export interface ModuleEventName {
  name: string
  short: string
}

export function stripColumns(
  showStepProgress = true,
  moduleNames?: ReadonlyMap<string, ModuleEventName>,
): Column<StripEventRow>[] {
  const stepCell: Record<string, (step: StepLogRow) => ReactNode> = {
    ts: (step) => <span className="muted mono num">{timeSecText(step.ts)}</span>,
    severity: () => <StatusBadge label={t('enum.severity.INFO')} status="INFO" />,
    category: () => <span className="muted">{t('strip.eventsKindStep')}</span>,
    operation: (step) => (
      <span className="mono">{step.action === undefined ? '' : operationText(step.action)}</span>
    ),
    message: (step) => (showStepProgress ? stepContentText(step) : <StepLogCell step={step} />),
  }
  // `compact` · 정렬(`sort` · `sortValue`) · 내보내기 글자(`exportValue`)는 원장 행 모양을 받는다 — 두 행 모양이 섞인 이 표에는 없으니 버리고 옮긴다
  const columns: Column<StripEventRow>[] = eventColumns().map(
    ({
      compact: _compact,
      sort: _sort,
      sortValue: _sortValue,
      exportValue: _exportValue,
      ...column
    }) => ({
      ...column,
      cell: (row: StripEventRow) =>
        row.kind === 'history' ? column.cell(row.entry) : (stepCell[column.key]?.(row.step) ?? ''),
    }),
  )
  if (moduleNames === undefined) return columns
  /* 열 폭은 `moduleEventLog.css` 의 고정 배치가 정한다 — `fit` · `fill` 을 걸면 그 배치에서 시각 · 장비 열이 내용을 밀어낸다 */
  return [
    { ...columns[0]! },
    {
      key: 'module',
      header: t('column.module'),
      nowrap: true,
      cell: (row) => {
        if (row.kind !== 'step' || row.step.moduleId === undefined) return ''
        const name = moduleNames.get(row.step.moduleId)
        const full = name?.name ?? row.step.moduleId
        /* 🔴 짧은 이름은 글자 노드가 아니라 CSS `content` 로 낸다 — 칸의 글자 값 · 스크린리더 · 내보내기는 언제나 전체 이름 하나다 */
        return (
          <span className="module-events__source" data-short={name?.short ?? full} title={full}>
            <span className="module-events__source-full">{full}</span>
          </span>
        )
      },
    },
    { ...columns[columns.length - 1]! },
  ]
}

export function stripRowKey(row: StripEventRow): string {
  if (row.kind === 'history') return eventRowKey(row.entry)
  const s = row.step
  if (s.receivedOrder !== undefined) return `step-received-${s.receivedOrder}`
  return `step-${s.ts}-${s.moduleId ?? ''}-${s.eventType}-${s.step ?? ''}`
}

export function isEmphasisRow(row: StripEventRow): boolean {
  return row.kind === 'history'
    ? isEmphasisEvent(row.entry)
    : row.step.eventType === 'error' || row.step.eventType === 'aborted'
}

/* ── 쪽 나눔(목록 화면 · `screens/listPaging.ts`) ───────────────────────── */

/** 전체 쪽 수. 0건이어도 1쪽이다 */
export function pageCountOf(total: number, size: number): number {
  return Math.max(1, Math.ceil(total / size))
}

/** 받은 목록에서 한 쪽(0부터). 목록 화면 쪽 나눔(`screens/listPaging.ts`)이 쓴다 */
export function pageSlice<T>(rows: readonly T[], page: number, size: number): T[] {
  return rows.slice(page * size, page * size + size)
}

/**
 * 붙잡은 행(`held`) 위에 새로 선 행 수 — 지금 목록(`rows` · 새것이 위)에서 붙잡은 맨 윗행이 몇 번째인가.
 * 🔴 붙잡은 맨 윗행이 지금 목록에 없으면(상한을 넘어 밀려났다) 지금 목록 전부가 새것이다. 붙잡은 것이 비었으면 전부.
 */
export function newRowsAbove(
  rows: readonly StripEventRow[],
  held: readonly StripEventRow[],
): number {
  const top = held[0]
  if (top === undefined) return rows.length
  const key = stripRowKey(top)
  const at = rows.findIndex((row) => stripRowKey(row) === key)
  return at < 0 ? rows.length : at
}
